"use client";

import React, { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";
import { AlertCircle, CheckCircle, Loader2, Lock } from "lucide-react";
import { trackError, trackFormSubmit } from "@utils/ga4Tracking";
import gateData from "@data/components/guide-gate.json";
import blockedDomains from "@data/config/guide-freemail-domains.json";

/**
 * GuideGate — the one-time e-mail wall of the Agentic BI Guide.
 *
 * The gated body is rendered into the server HTML (blurred and clipped for
 * readers who have not unlocked yet) and marked as paywalled in the Article
 * schema (`isAccessibleForFree: false` + `hasPart` → `.guide-gated`), so
 * search engines index the full text while readers see the wall.
 *
 * One e-mail unlocks every chapter: the access flag lives in a cookie and in
 * localStorage (90 days, refreshed on every visit). Three layers keep a
 * returning reader from ever seeing the wall flash:
 *
 *   1. The page reads the cookie on the server (`hasGuideAccess`) and passes
 *      `initialUnlocked`, so the HTML itself is already open.
 *   2. The client state is a `useSyncExternalStore` over cookie + storage:
 *      hydration matches the server HTML, a client-side navigation reads the
 *      flag before the first paint, and an unlock in another tab arrives via
 *      the `storage` event.
 *   3. If the cookie is gone but localStorage still holds the flag, an inline
 *      script runs before hydration, marks `<html data-guide-access="1">`
 *      (guide.css then shows the open state at first paint) and restores the
 *      cookie for the next request.
 */

interface GuideGateProps {
  children: React.ReactNode;
  chapterSlug: string;
  chapterTitle: string;
  /** Result of `hasGuideAccess()` on the server — the state the HTML is rendered in. */
  initialUnlocked: boolean;
}

// Same syntax rule as the API; the server stays the authority (it also
// checks that the domain accepts mail).
const EMAIL_PATTERN = /^[A-Za-z0-9._%+-]{1,64}@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,24}$/;
const FREEMAIL = new Set(blockedDomains.freemail);
const DISPOSABLE = new Set(blockedDomains.disposable);
const RELAY = new Set(blockedDomains.relay);

/** Client-side address check; returns the message to show, or null if fine. */
function addressProblem(trimmed: string): string | null {
  if (!EMAIL_PATTERN.test(trimmed) || trimmed.includes("..")) return gateData.invalidEmailMessage;
  const domain = trimmed.slice(trimmed.lastIndexOf("@") + 1);
  if (FREEMAIL.has(domain)) return gateData.freemailMessage;
  if (DISPOSABLE.has(domain)) return gateData.disposableMessage;
  if (RELAY.has(domain)) return gateData.relayMessage;
  return null;
}

const SERVER_ERROR_MESSAGES: Record<string, keyof typeof gateData> = {
  invalid_email: "invalidEmailMessage",
  freemail_not_allowed: "freemailMessage",
  disposable_not_allowed: "disposableMessage",
  relay_not_allowed: "relayMessage",
  invalid_domain: "invalidDomainMessage",
  consent_required: "consentRequiredMessage",
};

const ACCESS_CHANGE_EVENT = "dbi-guide-access-change";
const COOKIE_MAX_AGE = gateData.cookieDays * 24 * 60 * 60;

function readAccessFlag(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.localStorage.getItem(gateData.storageKey) === "1") return true;
  } catch {
    /* storage blocked — fall through to the cookie */
  }
  return document.cookie.split(";").some((part) => part.trim().startsWith(`${gateData.cookieName}=1`));
}

/** Cookie (what the server reads), localStorage (survives a lost cookie), html marker (CSS). */
function persistAccessFlag(): void {
  document.cookie = `${gateData.cookieName}=1; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax${
    window.location.protocol === "https:" ? "; Secure" : ""
  }`;
  try {
    window.localStorage.setItem(gateData.storageKey, "1");
  } catch {
    /* cookie is enough */
  }
  document.documentElement.setAttribute("data-guide-access", "1");
}

function subscribeToAccess(onChange: () => void): () => void {
  window.addEventListener(ACCESS_CHANGE_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(ACCESS_CHANGE_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

/**
 * Runs before hydration on a hard load. The server rendered the wall because
 * the cookie was missing; if localStorage still says "unlocked", mark <html>
 * so guide.css shows the open state at first paint, and put the cookie back
 * so the next request is rendered open on the server. Built from the JSON
 * keys so the three places (server, client, script) can never drift.
 */
const PRE_HYDRATION_SYNC =
  `(function(){try{if(localStorage.getItem(${JSON.stringify(gateData.storageKey)})!=="1")return;` +
  `document.documentElement.setAttribute("data-guide-access","1");` +
  `document.cookie=${JSON.stringify(gateData.cookieName)}+"=1; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax"` +
  `+(location.protocol==="https:"?"; Secure":"")}catch(e){}})();`;

/** Shared by the hub (status note) and the chapter gate. */
export function useGuideAccess(initialUnlocked: boolean): { unlocked: boolean; unlock: () => void } {
  // Hydration uses the server snapshot (what the cookie said when the HTML
  // was rendered); every other render reads the live flag, so a client-side
  // navigation never paints the wall for a reader who already unlocked.
  const unlocked = useSyncExternalStore(
    subscribeToAccess,
    () => initialUnlocked || readAccessFlag(),
    () => initialUnlocked
  );

  // Sliding 90 days, and the cookie comes back if only localStorage had it.
  useEffect(() => {
    if (unlocked) persistAccessFlag();
  }, [unlocked]);

  const unlock = useCallback(() => {
    persistAccessFlag();
    window.dispatchEvent(new Event(ACCESS_CHANGE_EVENT));
  }, []);

  return { unlocked, unlock };
}

export default function GuideGate({ children, chapterSlug, chapterTitle, initialUnlocked }: GuideGateProps) {
  const pathname = usePathname();
  const { unlocked, unlock } = useGuideAccess(initialUnlocked);
  // Only a real unlock in this view plays the un-blur; a returning reader
  // gets the open text without animation.
  const [justUnlocked, setJustUnlocked] = useState(false);
  const [email, setEmail] = useState("");
  const [optIn, setOptIn] = useState(false);
  const [delivered, setDelivered] = useState<boolean | null>(null);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const locked = !unlocked;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = email.trim().toLowerCase();
    // Address first, consent second — the reader always learns why nothing
    // happened, even with the box unticked.
    const problem = addressProblem(trimmed);
    if (problem) {
      setStatus("error");
      setMessage(problem);
      return;
    }
    // The opt-in is the consent the unlock rests on — no unlock without it.
    if (!optIn) {
      setStatus("error");
      setMessage(gateData.consentRequiredMessage);
      return;
    }

    setStatus("submitting");
    setMessage("");
    try {
      const response = await fetch("/api/guide-access", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmed,
          optIn,
          optInText: gateData.fields.optIn.label,
          chapter: chapterTitle,
          pagePath: pathname,
          source: gateData.webhook.source,
        }),
      });

      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as { error?: string };
        const messageKey = body.error ? SERVER_ERROR_MESSAGES[body.error] : undefined;
        if (messageKey) {
          setStatus("error");
          setMessage(String(gateData[messageKey]));
          return;
        }
        throw new Error(body.error || `HTTP ${response.status}`);
      }

      const result = (await response.json().catch(() => ({}))) as { delivered?: boolean };
      setDelivered(result.delivered !== false);
      trackFormSubmit("guide_access", "lead", true);
      setStatus("success");
      setMessage(gateData.successMessage);
      // Give the success line a beat before the text unblurs.
      window.setTimeout(() => {
        setJustUnlocked(true);
        unlock();
      }, 700);
    } catch (error) {
      trackError("guide_access_submit", error instanceof Error ? error.message : "unknown");
      setStatus("error");
      setMessage(gateData.errorMessage);
    }
  };

  return (
    <>
      {/* Inline on purpose: it has to run before hydration. React never
          re-executes it on hydration or client navigation, and in both of
          those cases the store above already knows the answer. */}
      {initialUnlocked ? null : (
        <script suppressHydrationWarning dangerouslySetInnerHTML={{ __html: PRE_HYDRATION_SYNC }} />
      )}
      <div
        className={`guide-gate ${locked ? "guide-gate--locked" : "guide-gate--open"}${
          justUnlocked ? " guide-gate--unblur" : ""
        }`}
        data-chapter={chapterSlug}
      >
        <div className="guide-gate__status" aria-live="polite">
          {locked ? (
            <span className="guide-gate__chip guide-gate__chip--locked">
              <Lock size={13} aria-hidden="true" />
              {gateData.lockedChip}
            </span>
          ) : (
            <span className="guide-gate__chip guide-gate__chip--unlocked">
              <CheckCircle size={13} aria-hidden="true" />
              {gateData.unlockedChip}
            </span>
          )}
          {/* Dev only: the API unlocked without a Slack webhook — keep that
              visible after the wall is gone, or nobody notices the gap. */}
          {delivered === false ? <p className="guide-gate__dev-note">{gateData.devNoWebhookNote}</p> : null}
        </div>

        {/* The full text is always in the DOM; `inert` keeps it out of the tab
            order and selection while the wall is up. React 18 needs the
            attribute as a string. */}
        <div
          className="guide-gated"
          aria-hidden={locked ? "true" : undefined}
          {...(locked ? ({ inert: "" } as Record<string, string>) : {})}
        >
          {children}
        </div>

        {locked ? (
          <div className="guide-gate__wall" role="region" aria-label={gateData.title}>
            <div className="guide-gate__card">
              <span className="eyebrow guide-gate__eyebrow">{gateData.eyebrow}</span>
              <h3 className="guide-gate__title">{gateData.title}</h3>
              <p className="guide-gate__description">{gateData.description}</p>
              <ul className="guide-gate__benefits">
                {gateData.benefits.map((benefit) => (
                  <li key={benefit}>
                    <span className="guide-gate__dot" aria-hidden="true" />
                    {benefit}
                  </li>
                ))}
              </ul>

              {status === "success" ? (
                <div className="guide-gate__success" role="status">
                  <CheckCircle size={20} aria-hidden="true" />
                  <div>
                    <strong>{gateData.successTitle}</strong>
                    <p>{message}</p>
                    {delivered === false ? (
                      <p className="guide-gate__dev-note">{gateData.devNoWebhookNote}</p>
                    ) : null}
                  </div>
                </div>
              ) : (
                <form className="guide-gate__form" onSubmit={handleSubmit} noValidate>
                  <label className="guide-gate__label" htmlFor={`guide-gate-email-${chapterSlug}`}>
                    {gateData.fields.email.label}
                  </label>
                  <div className="guide-gate__row">
                    <input
                      id={`guide-gate-email-${chapterSlug}`}
                      type="email"
                      name="email"
                      inputMode="email"
                      autoComplete={gateData.fields.email.autoComplete}
                      placeholder={gateData.fields.email.placeholder}
                      value={email}
                      onChange={(event) => {
                        setEmail(event.target.value);
                        if (status === "error") setStatus("idle");
                      }}
                      onBlur={() => {
                        const trimmed = email.trim().toLowerCase();
                        if (!trimmed) return;
                        const problem = addressProblem(trimmed);
                        if (problem) {
                          setStatus("error");
                          setMessage(problem);
                        }
                      }}
                      required
                      aria-invalid={status === "error" ? "true" : undefined}
                      className="guide-gate__input"
                    />
                    <button
                      type="submit"
                      className="btn btn-primary guide-gate__submit"
                      disabled={status === "submitting"}
                    >
                      {status === "submitting" ? (
                        <>
                          <Loader2 size={16} className="guide-gate__spinner" aria-hidden="true" />
                          {gateData.submittingLabel}
                        </>
                      ) : (
                        gateData.submitLabel
                      )}
                    </button>
                  </div>
                  <label className="guide-gate__consent">
                    <input
                      type="checkbox"
                      name="optIn"
                      checked={optIn}
                      onChange={(event) => setOptIn(event.target.checked)}
                      required={gateData.fields.optIn.required}
                      aria-required={gateData.fields.optIn.required ? "true" : undefined}
                    />
                    <span>{gateData.fields.optIn.label}</span>
                  </label>
                  {status === "error" ? (
                    <p className="guide-gate__error" role="alert">
                      <AlertCircle size={15} aria-hidden="true" />
                      {message}
                    </p>
                  ) : null}
                  <p
                    className="guide-gate__privacy"
                    dangerouslySetInnerHTML={{ __html: gateData.privacyNote }}
                  />
                </form>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}

/** Small note for the hub: tells returning readers the guide is already open. */
export function GuideAccessNote({ initialUnlocked }: { initialUnlocked: boolean }) {
  const { unlocked } = useGuideAccess(initialUnlocked);
  if (!unlocked) return null;
  return (
    <p className="guide-access-note" role="status">
      <CheckCircle size={16} aria-hidden="true" />
      {gateData.unlockedHubNote}
    </p>
  );
}
